import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import './edit-password.css';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import * as SwalHandled from '../../../../shared/utils/swalHandler';
import { showManualSuccessPushToast } from '../../../../shared/pusNotification/useManualToasts';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControlWithFocus,
  FormMessageWithValidation,
  useFormValidation,
} from '../../../../shared/components/form';
import { createEditPasswordSchema } from '../../../../shared/validations/setting';
import { isUserLoggedIn } from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { updateUser as updateUserAction } from '../../../../shared/actions/userActions';
import { CheckIcon, CircleIcon } from '../../../../assets/Icons/SVG';

const ModernEditPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const user = useSelector((state) => state.user?.user);

  const schema = useMemo(() => createEditPasswordSchema(), []);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const form = useForm({
    resolver,
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { validateAndFocus } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    if (!isLoggedIn) {
      navigate.toSignup();
    }
  }, [isLoggedIn, navigate]);

  const togglePasswordVisibility = useCallback((field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  }, []);

  const newPasswordValue = form.watch('newPassword');

  const passwordRequirements = useMemo(() => {
    const value = newPasswordValue || '';

    return {
      minLength: value.length >= 6,
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    };
  }, [newPasswordValue]);

  const handlePasswordUpdate = useCallback(
    async ({ currentPassword, newPassword }) => {
      const trimmedCurrent = currentPassword.trim();
      const trimmedNew = newPassword.trim();

      setIsSubmitting(true);

      try {
        const result = await dispatch(
          updateUserAction({
            old_password: trimmedCurrent,
            password: trimmedNew,
          })
        );

        if (!result?.success) {
          throw new Error(result?.error || 'Failed to update password. Please try again.');
        }

        form.reset({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setPasswordVisibility({
          current: false,
          new: false,
          confirm: false,
        });

        showManualSuccessPushToast(
          'Updated!',
          result?.message || 'Password updated successfully.',
          user
        );
      } catch (error) {
        await SwalHandled.showError(
          'Error',
          error?.response?.data?.message || error?.message || 'Failed to update password. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, form, user]
  );

  const handleFormSubmit = useCallback(
    async (values) => {
      const isValid = await validateAndFocus();
      if (!isValid) {
        return;
      }

      await handlePasswordUpdate(values);
    },
    [handlePasswordUpdate, validateAndFocus]
  );

  const handleCancel = useCallback(() => {
    form.reset({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordVisibility({
      current: false,
      new: false,
      confirm: false,
    });
    navigate.back();
  }, [form, navigate]);

  if (!isLoggedIn) {
    return null;
  }

  const isSubmitDisabled = isSubmitting || form.formState.isSubmitting || !form.formState.isValid;

  return (
    <div className="modern-edit-password-page">
      <ModernHeader
        customTitle="Change Password"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <main className="modern-edit-password-main">
        <div className="modern-edit-password-form-container">
          <Form {...form}>
            <form
              className="modern-edit-password-section"
              autoComplete="off"
              onSubmit={form.handleSubmit(handleFormSubmit)}
            >
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem className="modern-edit-password-field">
                    <FormLabel className="modern-edit-password-label" htmlFor="current-password">
                      Current Password
                    </FormLabel>
                    <FormControlWithFocus fieldName="currentPassword">
                      <div className="modern-edit-password-input-container">
                        <input
                          {...field}
                          id="current-password"
                          type={passwordVisibility.current ? 'text' : 'password'}
                          placeholder="Enter current password"
                          className="modern-edit-password-input"
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          className="modern-edit-password-toggle-btn"
                          onClick={() => togglePasswordVisibility('current')}
                          aria-label={passwordVisibility.current ? 'Hide password' : 'Show password'}
                        >
                          {/* <i className={`fa-regular ${passwordVisibility.current ? 'fa-eye-slash' : 'fa-eye'}`} /> */}
                          <FontAwesomeIcon icon={passwordVisibility.current ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    <FormMessageWithValidation fieldName="currentPassword" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem className="modern-edit-password-field">
                    <FormLabel className="modern-edit-password-label" htmlFor="new-password">
                      New Password
                    </FormLabel>
                    <FormControlWithFocus fieldName="newPassword">
                      <div className="modern-edit-password-input-container">
                        <input
                          {...field}
                          id="new-password"
                          type={passwordVisibility.new ? 'text' : 'password'}
                          placeholder="Enter new password"
                          className="modern-edit-password-input"
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          className="modern-edit-password-toggle-btn"
                          onClick={() => togglePasswordVisibility('new')}
                          aria-label={passwordVisibility.new ? 'Hide password' : 'Show password'}
                        >
                          {/* <i className={`fa-regular ${passwordVisibility.new ? 'fa-eye-slash' : 'fa-eye'}`} /> */}
                          <FontAwesomeIcon icon={passwordVisibility.new ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    <FormMessageWithValidation fieldName="newPassword" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="modern-edit-password-field">
                    <FormLabel className="modern-edit-password-label" htmlFor="confirm-password">
                      Confirm New Password
                    </FormLabel>
                    <FormControlWithFocus fieldName="confirmPassword">
                      <div className="modern-edit-password-input-container">
                        <input
                          {...field}
                          id="confirm-password"
                          type={passwordVisibility.confirm ? 'text' : 'password'}
                          placeholder="Confirm new password"
                          className="modern-edit-password-input"
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          className="modern-edit-password-toggle-btn"
                          onClick={() => togglePasswordVisibility('confirm')}
                          aria-label={passwordVisibility.confirm ? 'Hide password' : 'Show password'}
                        >
                          {/* <i className={`fa-regular ${passwordVisibility.confirm ? 'fa-eye-slash' : 'fa-eye'}`} /> */}
                          <FontAwesomeIcon icon={passwordVisibility.confirm ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    <FormMessageWithValidation fieldName="confirmPassword" />
                    <p className="modern-edit-password-help-text">
                      Password must include at least 6 characters, one uppercase letter, one number,
                      and one special character.
                    </p>
                  </FormItem>
                )}
              />

              <div className="modern-edit-password-requirements">
                <h3 className="modern-edit-password-requirements-title">Password requirements</h3>
                <ul className="modern-edit-password-requirements-list">
                  <li className={`modern-edit-password-requirement ${passwordRequirements.minLength ? 'met' : ''}`}>
                    {passwordRequirements.minLength ? <CheckIcon fill="#f534bf" /> : <CircleIcon fill="#a3a3a3" />}
                    <span>At least 6 characters</span>
                  </li>

                  <li className={`modern-edit-password-requirement ${passwordRequirements.uppercase ? 'met' : ''}`}>
                    {passwordRequirements.uppercase ? <CheckIcon fill="#f534bf" /> : <CircleIcon fill="#a3a3a3" />}
                    <span>One uppercase letter</span>
                  </li>

                  <li className={`modern-edit-password-requirement ${passwordRequirements.number ? 'met' : ''}`}>
                    {passwordRequirements.number ? <CheckIcon fill="#f534bf" /> : <CircleIcon fill="#a3a3a3" />}
                    <span>One number</span>
                  </li>

                  <li className={`modern-edit-password-requirement ${passwordRequirements.special ? 'met' : ''}`}>
                    {passwordRequirements.special ? <CheckIcon fill="#f534bf" /> : <CircleIcon fill="#a3a3a3" />}
                    <span>One special character</span>
                  </li>

                </ul>
              </div>

              <div className="modern-edit-password-action-buttons">
                <button type="submit" className="modern-edit-password-save-btn" disabled={isSubmitDisabled}>
                  {isSubmitting || form.formState.isSubmitting ? 'Saving...' : 'Save password'}
                </button>
                <button
                  type="button"
                  className="modern-edit-password-cancel-btn"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </Form>
        </div>
      </main>
    </div>
  );
};

export default ModernEditPassword; 